/**
 * V70 ISSUE-01 재제보 대응 — 리더보드 사용자 이름 override 유틸
 *
 * 목적:
 *   - 기존 저장된 리더보드 record 의 displayName 이 '이름 없음' 또는 '' 로 남아있는 경우에도
 *     실제 Firestore `users/{uid}` 의 nameKo 로 UI 표시가 되도록 override.
 *   - 저장된 record 는 수정하지 않음 (사용자 지시: 다른 사용자 기록 삭제 금지).
 *   - 전체 직원 명단 다운로드 금지 — Top 10 각 UID 10개만 조회 (최소 범위).
 *   - demo-* 샘플 UID 는 skip (실제 사용자 아님).
 *   - 신규 서버 API 추가 아님 — 기존 Firestore users 컬렉션 client read 범위.
 *
 * fail-open:
 *   - Firestore 조회 실패 (not exists / permission denied / network) 시 null 반환.
 *   - 호출부에서 fallback 체인 `nameMap.get(uid) || row.displayName || '-'` 적용.
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

/**
 * Top 10 UID 리스트에 대해 Firestore users 조회 → nameKo map 반환.
 *
 * - demo-* UID 는 skip (map 에 포함 안 함)
 * - 중복 UID 는 dedupe
 * - 조회 실패한 UID 는 map 에 포함 안 함 (fallback 체인에서 저장값 사용)
 */
export async function resolveUserNames(uids: string[]): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  // 테스트 환경 skip — Mock 전제 테스트 호환 (실 Firestore 호출 X)
  if (process.env.NODE_ENV === 'test') return result;
  if (!db) return result;
  const unique = Array.from(new Set(uids.filter((u) => !!u && !u.startsWith('demo-'))));
  if (unique.length === 0) return result;

  const settled = await Promise.allSettled(
    unique.map(async (uid) => {
      try {
        const snap = await getDoc(doc(db!, 'users', uid));
        if (!snap.exists()) return null;
        const data = snap.data() as any;
        const nameKo = typeof data?.nameKo === 'string' ? data.nameKo.trim() : '';
        if (nameKo) return { uid, nameKo };
        return null;
      } catch {
        return null;
      }
    })
  );
  for (const r of settled) {
    if (r.status === 'fulfilled' && r.value) {
      result.set(r.value.uid, r.value.nameKo);
    }
  }
  return result;
}

/**
 * 저장된 displayName 과 Firestore nameKo 를 체인으로 결합.
 *
 * - 1순위: Firestore nameKo (실시간 조회 결과)
 * - 2순위: record 저장 displayName (단, '이름 없음' 하드코딩 fallback 은 제외)
 * - 3순위: '-' (완전 미등록 표시, UI placeholder — 가짜 이름 생성 아님)
 */
export function resolveDisplayName(
  uid: string,
  savedDisplayName: string | null | undefined,
  nameMap: Map<string, string>,
): string {
  const fromFirestore = nameMap.get(uid);
  if (fromFirestore) return fromFirestore;
  const saved = typeof savedDisplayName === 'string' ? savedDisplayName.trim() : '';
  // ISSUE-01: 과거 저장 레코드에 '이름 없음' 하드코딩이 남아있을 수 있음 → UI 표시 시 제외
  if (saved && saved !== '이름 없음') return saved;
  return '-';
}
